import { useEffect, useState } from 'react'
import { api, type VerlaufBild } from '../api'
import { anzahl, datumKurz, datumMittel, euro, euroKurz, WOCHENTAGE_KURZ } from '../format'
import { Balken } from '../komponenten/Balken'
import { stundenLuecken } from '../komponenten/diagramm'
import { Karte } from '../komponenten/Karte'
import { Linie } from '../komponenten/Linie'

export function Verlauf({ von, bis, betriebe }: { von: string; bis: string; betriebe: string[] }) {
  const [d, setD] = useState<VerlaufBild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.verlauf(von, bis, betriebe)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  const lang = d.tage.length > 45
  const summe = d.tage.reduce((s, t) => s + Number(t.umsatz), 0)
  const schnitt = d.tage.length ? summe / d.tage.length : 0

  return (
    <>
      <Karte titel="Umsatz je Geschäftstag"
             neben={`${d.tage.length} Tage · Mittel ${euro(schnitt)}`}>
        {d.tage.length ? (
          <Linie
            punkte={d.tage.map((t) => ({
              schluessel: t.tag,
              etikett: lang ? datumKurz(t.tag) : datumMittel(t.tag),
              wert: Number(t.umsatz),
              neben: anzahl(t.rechnungen),
              ampel: t.ampel,
            }))}
            formatiere={euro} formatiereAchse={euroKurz} />
        ) : <div className="leer">Keine Geschäftstage im Zeitraum.</div>}
      </Karte>

      <div className="gitter g-2" style={{ marginTop: 14 }}>
        <Karte titel="Umsatz je Wochentag" neben="über den gewählten Zeitraum">
          {d.wochentage.length ? (
            <Balken
              werte={d.wochentage.map((w) => ({
                schluessel: String(w.wochentag),
                etikett: WOCHENTAGE_KURZ[w.wochentag - 1] ?? String(w.wochentag),
                wert: Number(w.umsatz),
                neben: `${anzahl(w.rechnungen)} auf ${w.tage} Tagen`,
              }))}
              formatiere={euro} formatiereAchse={euroKurz} />
          ) : <div className="leer">Keine Daten.</div>}
        </Karte>

        <Karte titel="Umsatz je Stunde" neben="Ortszeit des Standorts">
          {d.stunden.length ? (
            <Balken
              werte={stundenLuecken(d.stunden,
                (h) => ({ stunde: h, umsatz: '0', rechnungen: 0 }),
              ).map((h) => ({
                schluessel: String(h.stunde),
                etikett: String(h.stunde).padStart(2, '0'),
                wert: Number(h.umsatz),
                neben: anzahl(h.rechnungen),
              }))}
              formatiere={euro} formatiereAchse={euroKurz} />
          ) : <div className="leer">Keine Daten.</div>}
        </Karte>
      </div>

      <Karte titel="Die Tage im Einzelnen" flach>
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Geschäftstag</th>
                <th className="r">Rechnungen</th>
                <th className="r">Ø Rechnung</th>
                <th className="r">Umsatz</th>
              </tr>
            </thead>
            <tbody>
              {[...d.tage].reverse().map((t) => (
                <tr key={t.tag}>
                  <td>{datumMittel(t.tag)}</td>
                  <td className="r zahl dezent">{anzahl(t.rechnungen)}</td>
                  <td className="r zahl">{euro(t.durchschnittsbon)}</td>
                  <td className="r zahl">{euro(t.umsatz)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Karte>
    </>
  )
}
